// client/src/components/vendas/PDVModal.js - VERSÃO CORRIGIDA
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../context/auth/AuthProvider';
import useAlert from '../../hooks/useAlert';
import { useItensVendaveisForPDV } from '../../hooks/useItensVendaveis';
import { useMutateVendas } from '../../hooks/useVendas';
import { useMetodosPagamentoDaConta } from '../../hooks/useVendaFormData';
import { useClientesDaConta } from '../../hooks/useVendaFormData';
import { useEnderecosDaConta } from '../../hooks/useVendaFormData';
import { useTenantI18n } from '../../hooks/useAccounts';
import { formatCurrency as formatCurrencyUtil } from '../../utils/formatters';
import {
    X,
    Plus,
    Minus,
    Trash2,
    ShoppingCart,
    User,
    CreditCard,
    DollarSign,
    Barcode,
    CheckCircle,
    ArrowLeft,
    ArrowRight,
    Building,
    Gift,
    Box,
    Clock,
    Package,
    Search,
    Filter,
    Star,
    RefreshCcw
} from 'lucide-react';

// ✅ CORREÇÃO: Funções auxiliares movidas para fora do componente
const getIconByCategory = (categoria) => {
    switch (categoria) {
        case 'Produto':
            return <Package size={24} className="text-gray-400" />;
        case 'Serviço':
            return <Clock size={24} className="text-gray-400" />;
        case 'Locação':
            return <Building size={24} className="text-gray-400" />;
        case 'Combo':
            return <Gift size={24} className="text-gray-400" />;
        case 'Pacote':
            return <Box size={24} className="text-gray-400" />;
        case 'Pacote Cliente':
            return <Star size={24} className="text-gray-400" />;
        default:
            return <ShoppingCart size={24} className="text-gray-400" />;
    }
};

const getPaymentIcon = (tipo) => {
    switch (tipo) {
        case 'Dinheiro':
            return <DollarSign size={16} />;
        case 'Cartão de Crédito':
        case 'Cartão de Débito':
            return <CreditCard size={16} />;
        case 'PIX':
            return <Barcode size={16} />;
        default:
            return <CreditCard size={16} />;
    }
};

const PDVModal = ({ onClose, onSaveSuccess }) => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const { currency, locale } = useTenantI18n();
    const accountId = user?.account?._id;

    // ✅ CORREÇÃO: Usar hook com fallback para PDV
    const {
        data: itensVendaveis = [],
        isLoading: loadingItens,
        error: errorItens,
        categorias = [],
        status: hookStatus
    } = useItensVendaveisForPDV(accountId, {
        enabled: !!accountId
    });

    const { data: metodosPagamentoData } = useMetodosPagamentoDaConta({
        enabled: !!accountId
    });
    const { data: clientesData } = useClientesDaConta({
        enabled: !!accountId
    });
    const { data: enderecosData } = useEnderecosDaConta({
        enabled: !!accountId
    });
    
    const { createVenda } = useMutateVendas();
    const isCreating = createVenda.isPending;

    // Estados do PDV
    const [clienteSelecionado, setClienteSelecionado] = useState(null);
    const [carrinho, setCarrinho] = useState([]);
    const [metodoPagamento, setMetodoPagamento] = useState('');
    const [observacoes, setObservacoes] = useState('');
    const [desconto, setDesconto] = useState(0);
    const [busca, setBusca] = useState('');
    const [etapaAtual, setEtapaAtual] = useState('produtos');
    const [categoriaAtiva, setCategoriaAtiva] = useState('Todos');
    const [filtroPreco, setFiltroPreco] = useState('todos');
    const [itensFavoritos, setItensFavoritos] = useState(new Set());
    const [enderecoSelecionado, setEnderecoSelecionado] = useState('');

    // ✅ CORREÇÃO: Debug para verificar itens carregados
    useEffect(() => {
        console.log('🔍 PDVModal - Itens carregados:', {
            total: itensVendaveis.length,
            porCategoria: itensVendaveis.reduce((acc, item) => {
                acc[item.categoria] = (acc[item.categoria] || 0) + 1;
                return acc;
            }, {}),
            primeirosItens: itensVendaveis.slice(0, 3).map(item => ({
                id: item.id,
                nome: item.nome,
                categoria: item.categoria,
                modelo: item.modelo,
                preco: item.preco
            }))
        });
    }, [itensVendaveis]);

    // ✅ CORREÇÃO: Declarar limparPDV ANTES de ser usada
    const limparPDV = useCallback(() => {
        setCarrinho([]);
        setClienteSelecionado(null);
        setMetodoPagamento('');
        setObservacoes('');
        setDesconto(0);
        setBusca('');
        setEtapaAtual('produtos');
        setCategoriaAtiva('Todos');
        setFiltroPreco('todos');
        setItensFavoritos(new Set());
        setEnderecoSelecionado('');
    }, []);

    // Cliente padrão da conta
    const defaultClientId = useMemo(() => {
        return user?.account?.clientePadrao?._id || user?.account?.clientePadrao || null;
    }, [user]);

    // Métodos de pagamento disponíveis
    const metodosPagamento = useMemo(() => {
        return metodosPagamentoData || [];
    }, [metodosPagamentoData]);

    // Clientes disponíveis
    const clientes = useMemo(() => {
        return clientesData || [];
    }, [clientesData]);

    // Endereços disponíveis
    const enderecos = useMemo(() => {
        return enderecosData || [];
    }, [enderecosData]);

    // ✅ CORREÇÃO: Filtrar itens baseado na busca, categoria e filtros - MANTIDO ORIGINAL
    const itensFiltrados = useMemo(() => {
        if (!itensVendaveis || !Array.isArray(itensVendaveis)) return [];

        let filtrados = [...itensVendaveis];

        // Filtro de busca
        if (busca) {
            const termo = busca.toLowerCase();
            filtrados = filtrados.filter(item =>
                item.nome?.toLowerCase().includes(termo) ||
                item.descricao?.toLowerCase().includes(termo) ||
                item.categoria?.toLowerCase().includes(termo)
            );
        }

        // Filtro de categoria
        if (categoriaAtiva !== 'Todos') {
            filtrados = filtrados.filter(item => item.categoria === categoriaAtiva);
        }

        // Filtro de preço
        if (filtroPreco !== 'todos') {
            switch (filtroPreco) {
                case 'gratis':
                    filtrados = filtrados.filter(item => item.preco === 0);
                    break;
                case 'ate50':
                    filtrados = filtrados.filter(item => item.preco > 0 && item.preco <= 50);
                    break;
                case '50a100':
                    filtrados = filtrados.filter(item => item.preco > 50 && item.preco <= 100);
                    break;
                case 'acima100':
                    filtrados = filtrados.filter(item => item.preco > 100);
                    break;
                default:
                    break;
            }
        }

        // Ordenar por favoritos primeiro, depois por nome
        filtrados.sort((a, b) => {
            const aFavorito = itensFavoritos.has(a.id);
            const bFavorito = itensFavoritos.has(b.id);
            
            if (aFavorito && !bFavorito) return -1;
            if (!aFavorito && bFavorito) return 1;
            return a.nome?.localeCompare(b.nome) || 0;
        });

        return filtrados;
    }, [itensVendaveis, busca, categoriaAtiva, filtroPreco, itensFavoritos]);

    // Cálculos do carrinho
    const calculos = useMemo(() => {
        const subtotal = carrinho.reduce((total, item) => total + (item.preco * item.quantidade), 0);
        const validDesconto = Math.min(desconto, subtotal);
        const total = Math.max(0, subtotal - validDesconto);

        return {
            subtotal,
            desconto: validDesconto,
            total,
            quantidadeItens: carrinho.reduce((acc, item) => acc + item.quantidade, 0)
        };
    }, [carrinho, desconto]);

    // Adicionar item ao carrinho
    const adicionarAoCarrinho = useCallback((item) => {
        if (!item.disponivel) {
            setAlert(`${item.nome} não está disponível no momento.`, 'warning');
            return;
        }

        const itemExistente = carrinho.find(carrinhoItem => 
            carrinhoItem.id === item.id && 
            carrinhoItem.modelo === item.modelo
        );

        if (itemExistente) {
            // Atualizar quantidade se já existe no carrinho
            setCarrinho(prev => prev.map(carrinhoItem =>
                carrinhoItem.id === item.id && carrinhoItem.modelo === item.modelo
                    ? { ...carrinhoItem, quantidade: carrinhoItem.quantidade + 1 }
                    : carrinhoItem
            ));
        } else {
            // Adicionar novo item ao carrinho
            const novoItem = {
                id: item.id,
                modelo: item.modelo,
                nome: item.nome,
                descricao: item.descricao,
                preco: item.preco || 0,
                precoBase: item.precoBase || 0,
                quantidade: 1,
                imagemUrl: item.imagemUrl,
                categoria: item.categoria,
                metadata: item.metadata || {},
                requerAgendamento: item.requerAgendamento || false,
                unidade: item.unidade || 'unidade'
            };
            setCarrinho(prev => [...prev, novoItem]);
        }

        // Feedback visual
        setBusca('');
    }, [carrinho, setAlert]);

    // Remover item do carrinho
    const removerDoCarrinho = useCallback((itemId) => {
        setCarrinho(prev => prev.filter(item => item.id !== itemId));
    }, []);

    // Atualizar quantidade no carrinho
    const atualizarQuantidade = useCallback((itemId, novaQuantidade) => {
        const quantidade = Math.max(0, novaQuantidade);
        
        if (quantidade === 0) {
            removerDoCarrinho(itemId);
            return;
        }

        setCarrinho(prev => prev.map(item =>
            item.id === itemId
                ? { ...item, quantidade }
                : item
        ));
    }, [removerDoCarrinho]);

    // Toggle favorito
    const toggleFavorito = useCallback((itemId) => {
        setItensFavoritos(prev => {
            const novosFavoritos = new Set(prev);
            if (novosFavoritos.has(itemId)) {
                novosFavoritos.delete(itemId);
            } else {
                novosFavoritos.add(itemId);
            }
            return novosFavoritos;
        });
    }, []);

    // ✅ CORREÇÃO: Finalizar venda com endereço
    const finalizarVenda = useCallback(async () => {
        if (carrinho.length === 0) {
            setAlert('Adicione itens ao carrinho antes de finalizar a venda.', 'warning');
            return;
        }

        if (!metodoPagamento) {
            setAlert('Selecione um método de pagamento.', 'warning');
            return;
        }

        const clientIdToSend = clienteSelecionado?._id || defaultClientId;

        if (!clientIdToSend) {
            setAlert('Erro: Cliente padrão não configurado para esta conta. Contate o suporte.', 'danger');
            return;
        }

        const unidadeIdToSend = enderecoSelecionado || enderecos[0]?._id;

        if (!unidadeIdToSend) {
            setAlert('Erro: Nenhuma unidade disponível para esta venda.', 'danger');
            return;
        }

        const vendaData = {
            cliente: clientIdToSend,
            unidade: unidadeIdToSend,
            itens: carrinho.map(item => ({
                produto: item.id,
                modelo: item.modelo,
                descricao: item.nome || 'Item sem nome',
                quantidade: item.quantidade || 1,
                precoUnitario: item.preco || 0,
                metadata: item.metadata || {}
            })),
            subtotal: calculos.subtotal || 0,
            descontoGeral: calculos.desconto || 0,
            total: calculos.total || 0,
            pagamentos: [{
                metodo: metodoPagamento,
                valor: calculos.total || 0,
                status: 'Pago',
                dataPagamento: new Date().toISOString()
            }],
            statusVenda: 'Finalizada',
            statusPagamento: 'Pago',
            observacoes: observacoes || `Venda PDV - ${new Date().toLocaleString('pt-BR')}`,
            tipoVenda: 'pdv',
        };

        try {
            await createVenda.mutateAsync(vendaData);
            setAlert('Venda finalizada com sucesso!', 'success');
            onSaveSuccess();
            limparPDV();
        } catch (error) {
            console.error('Erro ao finalizar venda:', error);
            const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido ao finalizar venda.';
            setAlert(`Erro ao finalizar venda: ${errorMessage}`, 'danger');
        }
    }, [
        carrinho,
        clienteSelecionado,
        metodoPagamento,
        calculos,
        observacoes,
        createVenda,
        onSaveSuccess,
        defaultClientId,
        enderecoSelecionado,
        enderecos,
        limparPDV,
        setAlert
    ]);

    // ✅ ACHADO: fixo em BRL, sem ler configuracoesGerais.i18n da conta.
    const formatCurrency = (value) => formatCurrencyUtil(value || 0, currency, locale);

    // Loading state
    if (loadingItens) {
        return (
            <div className="pdv-modal-overlay">
                <div className="pdv-modal-container items-center justify-center">
                    <div className="pdv-loading">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-700 text-center">Carregando produtos e serviços...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Error state - apenas para erros críticos
    if (errorItens) {
        return (
            <div className="pdv-modal-overlay">
                <div className="pdv-modal-container items-center justify-center">
                    <div className="error-state">
                        <h3 className="text-red-600 font-semibold mb-2">Erro ao carregar itens</h3>
                        <p className="text-gray-600 mb-4">{errorItens.message}</p>
                        {/* Antes reinventava cor do zero em Tailwind (bg-blue-600) */}
                        <button
                            onClick={() => window.location.reload()}
                            className="action-button action-button-primary"
                        >
                            <RefreshCcw size={18} className="icon" />
                            Tentar Novamente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pdv-modal-overlay">
            <div className="pdv-modal-container">
                {/* Header */}
                <div className="pdv-modal-header">
                    <div className="flex items-center gap-3">
                        <ShoppingCart size={32} className="text-white" />
                        <div>
                            <h2 className="text-xl font-bold text-white">Ponto de Venda - PDV</h2>
                            <p className="text-sm text-blue-100">
                                {Array.isArray(itensVendaveis) ? itensVendaveis.length : 0} itens disponíveis
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="bg-white bg-opacity-20 px-3 py-1 rounded-full text-white">
                            <span className="font-semibold">{calculos.quantidadeItens} itens</span>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                            aria-label="Fechar PDV"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>

                {/* Steps */}
                <div className="pdv-steps">
                    <div className={`step ${etapaAtual === 'produtos' ? 'active' : ''}`}>
                        <div className="step-number">1</div>
                        <span>Produtos</span>
                    </div>
                    <div className="step-connector"></div>
                    <div className={`step ${etapaAtual === 'checkout' ? 'active' : ''}`}>
                        <div className="step-number">2</div>
                        <span>Pagamento</span>
                    </div>
                </div>

                {/* Main Content */}
                <div className="pdv-main-content">
                    {etapaAtual === 'produtos' && (
                        <>
                            {/* Coluna de Produtos */}
                            <div className="pdv-products-column">
                                {/* Barra de Busca e Filtros */}
                                <div className="pdv-search-section">
                                    <div className="relative mb-4">
                                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                                        <input
                                            type="text"
                                            placeholder="Buscar produtos, serviços, locações..."
                                            value={busca}
                                            onChange={(e) => setBusca(e.target.value)}
                                            className="pdv-search-input"
                                        />
                                        {busca && (
                                            <button
                                                onClick={() => setBusca('')}
                                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            >
                                                <X size={16} />
                                            </button>
                                        )}
                                    </div>

                                    {/* Filtros Rápidos */}
                                    <div className="filters-grid">
                                        <div className="filter-group">
                                            <label className="filter-label">
                                                <Filter size={14} />
                                                Categoria
                                            </label>
                                            <select
                                                value={categoriaAtiva}
                                                onChange={(e) => setCategoriaAtiva(e.target.value)}
                                                className="filter-select"
                                            >
                                                <option value="Todos">Todas as categorias</option>
                                                {Array.isArray(categorias) && categorias.map(cat => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filter-group">
                                            <label className="filter-label">Preço</label>
                                            <select
                                                value={filtroPreco}
                                                onChange={(e) => setFiltroPreco(e.target.value)}
                                                className="filter-select"
                                            >
                                                <option value="todos">Todos os preços</option>
                                                <option value="gratis">Grátis</option>
                                                <option value="ate50">Até R$ 50</option>
                                                <option value="50a100">R$ 50 a R$ 100</option>
                                                <option value="acima100">Acima de R$ 100</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Grid de Produtos */}
                                <div className="pdv-products-grid">
                                    {itensFiltrados.length === 0 ? (
                                        <div className="empty-state">
                                            <Package size={48} className="text-gray-300 mx-auto mb-3" />
                                            <p className="text-gray-500 text-center">Nenhum item encontrado</p>
                                            <p className="text-sm text-gray-400 text-center">
                                                Tente ajustar os filtros ou buscar por outro termo
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="products-grid">
                                            {itensFiltrados.map((item) => (
                                                <div key={`${item.modelo}-${item.id}`} className="product-card">
                                                    <div className="product-card-header">
                                                        <div className="product-image">
                                                            {item.imagemUrl ? (
                                                                <img 
                                                                    src={item.imagemUrl} 
                                                                    alt={item.nome}
                                                                    className="product-img"
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className="product-placeholder">
                                                                {getIconByCategory(item.categoria)}
                                                            </div>
                                                        </div>
                                                        <button
                                                            onClick={() => toggleFavorito(item.id)}
                                                            className={`favorite-btn ${itensFavoritos.has(item.id) ? 'favorited' : ''}`}
                                                        >
                                                            <Star 
                                                                size={16} 
                                                                fill={itensFavoritos.has(item.id) ? "currentColor" : "none"}
                                                            />
                                                        </button>
                                                    </div>

                                                    <div className="product-card-body">
                                                        <h4 className="product-name">{item.nome || 'Item sem nome'}</h4>
                                                        <p className="product-description">
                                                            {item.descricao || 'Sem descrição'}
                                                        </p>
                                                        
                                                        <div className="product-metadata">
                                                            {item.requerAgendamento && (
                                                                <span className="metadata-tag agendamento">
                                                                    Requer agendamento
                                                                </span>
                                                            )}
                                                            {item.unidade && item.unidade !== 'unidade' && (
                                                                <span className="metadata-tag unidade">
                                                                    {item.unidade}
                                                                </span>
                                                            )}
                                                            {!item.disponivel && (
                                                                <span className="metadata-tag indisponivel">
                                                                    Indisponível
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="product-card-footer">
                                                        <div className="product-price">
                                                            {item.preco === 0 ? (
                                                                <span className="price-free">Grátis</span>
                                                            ) : (
                                                                <span className="price-value">
                                                                    {formatCurrency(item.preco)}
                                                                </span>
                                                            )}
                                                            {item.precoBase > item.preco && (
                                                                <span className="price-original">
                                                                    {formatCurrency(item.precoBase)}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <button
                                                            onClick={() => adicionarAoCarrinho(item)}
                                                            disabled={!item.disponivel}
                                                            className={`add-to-cart-btn ${!item.disponivel ? 'disabled' : ''}`}
                                                        >
                                                            <Plus size={16} />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Coluna do Carrinho */}
                            <div className="pdv-cart-column">
                                <div className="cart-section">
                                    <h3 className="section-title">
                                        <ShoppingCart size={20} />
                                        Carrinho de Vendas
                                        <span className="cart-badge">{calculos.quantidadeItens}</span>
                                    </h3>

                                    <div className="cart-items-container">
                                        {carrinho.length === 0 ? (
                                            <div className="empty-cart">
                                                <ShoppingCart size={48} className="text-gray-300 mb-3" />
                                                <p className="text-gray-500">Carrinho vazio</p>
                                                <p className="text-sm text-gray-400">Adicione itens da lista</p>
                                            </div>
                                        ) : (
                                            <div className="cart-items-list">
                                                {carrinho.map((item) => (
                                                    <div key={item.id} className="cart-item">
                                                        <div className="cart-item-info">
                                                            <div className="cart-item-header">
                                                                <span className="cart-item-name">{item.nome}</span>
                                                                <span className="cart-item-category">{item.categoria}</span>
                                                            </div>
                                                            <span className="cart-item-unit-price">
                                                                {formatCurrency(item.preco)}/{item.unidade}
                                                            </span>
                                                        </div>
                                                        <div className="cart-item-actions">
                                                            <div className="quantity-controls">
                                                                <button
                                                                    onClick={() => atualizarQuantidade(item.id, item.quantidade - 1)}
                                                                    className="quantity-button"
                                                                >
                                                                    <Minus size={14} />
                                                                </button>
                                                                <span className="quantity-display">{item.quantidade}</span>
                                                                <button
                                                                    onClick={() => atualizarQuantidade(item.id, item.quantidade + 1)}
                                                                    className="quantity-button"
                                                                >
                                                                    <Plus size={14} />
                                                                </button>
                                                            </div>
                                                            <div className="cart-item-price">
                                                                {formatCurrency(item.preco * item.quantidade)}
                                                            </div>
                                                            <button
                                                                onClick={() => removerDoCarrinho(item.id)}
                                                                className="remove-item-button"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {carrinho.length > 0 && (
                                        <>
                                            <div className="cart-summary">
                                                <div className="summary-row">
                                                    <span>Subtotal:</span>
                                                    <span>{formatCurrency(calculos.subtotal)}</span>
                                                </div>
                                                <div className="summary-row">
                                                    <span>Desconto:</span>
                                                    <div className="discount-input">
                                                        <span>R$</span>
                                                        <input
                                                            type="number"
                                                            value={desconto}
                                                            onChange={(e) => {
                                                                const val = Number(e.target.value);
                                                                setDesconto(Math.max(0, Math.min(val, calculos.subtotal)));
                                                            }}
                                                            min="0"
                                                            step="0.01"
                                                            max={calculos.subtotal}
                                                            className="w-20"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="summary-row total">
                                                    <span>Total:</span>
                                                    <span className="total-amount">
                                                        {formatCurrency(calculos.total)}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="cart-actions">
                                                <button
                                                    onClick={() => setEtapaAtual('checkout')}
                                                    className="next-step-button"
                                                    disabled={carrinho.length === 0}
                                                >
                                                    Ir para Pagamento
                                                    <ArrowRight size={18} />
                                                </button>
                                                <button
                                                    onClick={limparPDV}
                                                    className="clear-cart-button"
                                                >
                                                    <Trash2 size={16} />
                                                    Limpar Tudo
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </>
                    )}

                    {etapaAtual === 'checkout' && (
                        <div className="pdv-checkout-container">
                            <div className="checkout-header">
                                <button
                                    onClick={() => setEtapaAtual('produtos')}
                                    className="back-button"
                                >
                                    <ArrowLeft size={18} />
                                    Voltar
                                </button>
                                <h3 className="checkout-title">Finalizar Venda</h3>
                                <div style={{ width: '80px' }}></div>
                            </div>

                            <div className="checkout-content">
                                {/* Cliente */}
                                <div className="checkout-section">
                                    <h4 className="section-subtitle">
                                        <User size={18} /> Cliente
                                    </h4>
                                    <select
                                        value={clienteSelecionado?._id || ''}
                                        onChange={(e) => {
                                            const selectedId = e.target.value;
                                            const cliente = clientes.find(c => c._id === selectedId) || null;
                                            setClienteSelecionado(cliente);
                                        }}
                                        className="checkout-select"
                                    >
                                        <option value="">Consumidor Final</option>
                                        {Array.isArray(clientes) && clientes.map(cliente => (
                                            <option key={cliente._id} value={cliente._id}>
                                                {cliente.nome} {cliente.cpf ? `(${cliente.cpf})` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* ✅ CORREÇÃO: Endereço */}
                                {enderecos.length > 0 && (
                                    <div className="checkout-section">
                                        <h4 className="section-subtitle">
                                            <Building size={18} /> Endereço
                                        </h4>
                                        <select
                                            value={enderecoSelecionado || enderecos[0]?._id || ''}
                                            onChange={(e) => setEnderecoSelecionado(e.target.value)}
                                            className="checkout-select"
                                        >
                                            {enderecos.map(endereco => (
                                                <option key={endereco._id} value={endereco._id}>
                                                    {endereco.nome} - {endereco.localidade}, {endereco.uf}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {/* Resumo do Pedido */}
                                <div className="checkout-section">
                                    <h4 className="section-subtitle">Resumo do Pedido</h4>
                                    <div className="order-summary">
                                        {carrinho.map((item) => (
                                            <div key={item.id} className="order-item">
                                                <div className="order-item-info">
                                                    <span>{item.nome} x{item.quantidade}</span>
                                                    <span className="order-item-category">{item.categoria}</span>
                                                </div>
                                                <span>{formatCurrency(item.preco * item.quantidade)}</span>
                                            </div>
                                        ))}
                                        <div className="summary-divider"></div>
                                        <div className="summary-row">
                                            <span>Subtotal:</span>
                                            <span>{formatCurrency(calculos.subtotal)}</span>
                                        </div>
                                        {calculos.desconto > 0 && (
                                            <div className="summary-row discount">
                                                <span>Desconto:</span>
                                                <span>- {formatCurrency(calculos.desconto)}</span>
                                            </div>
                                        )}
                                        <div className="summary-row total">
                                            <span>Total:</span>
                                            <span>{formatCurrency(calculos.total)}</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Método de Pagamento */}
                                <div className="checkout-section">
                                    <h4 className="section-subtitle">
                                        <CreditCard size={18} /> Pagamento
                                    </h4>
                                    <div className="payment-methods-grid">
                                        {Array.isArray(metodosPagamento) && metodosPagamento.map(metodo => (
                                            <button
                                                key={metodo._id}
                                                onClick={() => setMetodoPagamento(metodo._id)}
                                                className={`payment-method ${metodoPagamento === metodo._id ? 'selected' : ''}`}
                                            >
                                                {getPaymentIcon(metodo.tipo)}
                                                <span>{metodo.nome}</span>
                                            </button>
                                        ))}
                                    </div>
                                    {!metodoPagamento && (
                                        <p className="validation-error">Selecione um método de pagamento</p>
                                    )}
                                </div>

                                {/* Observações */}
                                <div className="checkout-section">
                                    <h4 className="section-subtitle">Observações</h4>
                                    <textarea
                                        value={observacoes}
                                        onChange={(e) => setObservacoes(e.target.value)}
                                        placeholder="Observações sobre a venda (opcional)"
                                        className="observations-input"
                                        rows="3"
                                    />
                                </div>

                                {/* Ações */}
                                <div className="checkout-actions">
                                    <button
                                        onClick={() => setEtapaAtual('produtos')}
                                        className="cancel-button"
                                    >
                                        <ArrowLeft size={18} />
                                        Voltar
                                    </button>
                                    <button
                                        onClick={finalizarVenda}
                                        disabled={!metodoPagamento || isCreating || carrinho.length === 0}
                                        className="finish-button"
                                    >
                                        {isCreating ? (
                                            <>
                                                <div className="spinner"></div>
                                                Processando...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={18} />
                                                Finalizar Venda
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDVModal;